import React, { useMemo } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Col, Row, Form } from '@themesberg/react-bootstrap';
import { END_POINT_INDUSTRIES } from "../const";
import { showSuccessToast } from "../utils/toast";
import EditFormLayout from "../components/EditFormLayout";
import RecordEditActions from "../components/RecordEditActions";
import RecordEditFeedback from "../components/RecordEditFeedback";
import { FormikSearchableSelect, buildSelectOptions } from "../components/SearchableSelect";
import CompanyCheckboxPicker from "../components/CompanyCheckboxPicker";
import useRecordEditor from "../hooks/useRecordEditor";

const RELATED_ENDPOINTS = { context: 'auth/context', industries: END_POINT_INDUSTRIES };

export const IndustryEditForm = ({ industryId, onSuccess, onCancel, onSubmittingChange }) => {
  const editor = useRecordEditor({
    recordId: industryId,
    endpoint: END_POINT_INDUSTRIES,
    label: "industry",
    relatedEndpoints: RELATED_ENDPOINTS,
    onSuccess,
    onSubmittingChange,
  });
  const industry = editor.record;
  const companies = editor.related.context?.companies || [];
  const allowMultipleCompanies = Boolean(editor.related.context?.allow_multiple_companies);
  const parentIndustryOptions = useMemo(() => [
    { value: '0', label: 'No parent (top-level industry)' },
    ...buildSelectOptions(editor.related.industries || [], {
      filter: item => String(item.id) !== String(industryId),
    }),
  ], [editor.related.industries, industryId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: industry?.name || "",
      parent_id: industry?.parent_id?.toString() || "0",
      company_ids: (industry?.companies || []).map(company => String(company.id)),
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Industry name is required"),
      company_ids: allowMultipleCompanies ? Yup.array().min(1, "Select at least one company") : Yup.array(),
    }),
    onSubmit: editor.submit,
  });

  if (editor.loading || editor.loadError || !industry) {
    return <RecordEditFeedback loading={editor.loading} error={editor.loadError} label="industry"
      onRetry={editor.retry} onCancel={onCancel} />;
  }

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <Row>
        {industry.parent_id !== 0 && (
          <Col sm={6} className="mb-3">
            <Form.Group controlId="edit-industry-parent">
              <Form.Label>Parent Industry (optional)</Form.Label>
              <FormikSearchableSelect
                formik={formik}
                name="parent_id"
                inputId="edit-industry-parent"
                options={parentIndustryOptions}
                placeholder="Search parent industry..."
                isDisabled={formik.isSubmitting}
                menuPortalTarget={onCancel ? null : undefined}
              />
            </Form.Group>
          </Col>
        )}
        <Col md={6} className="mb-3">
          <Form.Group controlId="edit-industry-name">
            <Form.Label>Industry Name <span className="text-danger">*</span></Form.Label>
            <Form.Control type="text" autoFocus disabled={formik.isSubmitting}
              {...formik.getFieldProps("name")} isInvalid={formik.touched.name && formik.errors.name} />
            <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        {(allowMultipleCompanies || companies.length > 1) && (
          <Col md={12} className="mb-3">
            <CompanyCheckboxPicker companies={companies} selectedIds={formik.values.company_ids}
              onChange={companyIds => formik.setFieldValue("company_ids", companyIds)}
              error={formik.touched.company_ids && formik.errors.company_ids} disabled={formik.isSubmitting} />
          </Col>
        )}
      </Row>
      <RecordEditActions formik={formik} backTo="/industries" onCancel={onCancel} />
    </Form>
  );
};

export const EditIndustryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const handleSuccess = result => {
    showSuccessToast(result.message);
    navigate("/industries");
  };

  return (
    <EditFormLayout title="Edit Industry" subtitle="Update industry details" backTo="/industries">
      <IndustryEditForm key={id} industryId={id} onSuccess={handleSuccess} />
    </EditFormLayout>
  );
};
